import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '@/components/Header';
import BalanceChart from '@/components/BalanceChart';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { TrendingUp, Trash2, Plus, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const ExchangeSettings = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [balances, setBalances] = useState({});
  const [balanceHistory, setBalanceHistory] = useState({});
  const [overallHistory, setOverallHistory] = useState([]);
  const [selectedPeriods, setSelectedPeriods] = useState({});
  const [loadingConnections, setLoadingConnections] = useState(true);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [formData, setFormData] = useState({
    exchange_name: 'okx',
    key_name: '',
    api_key: '',
    api_secret: '',
    testnet: true
  });

  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      fetchConnections();
    }
  }, [user]);

  // Auto-refresh balance history every 5 minutes
  useEffect(() => {
    if (connections.length > 0) {
      fetchAllBalanceHistory();
      
      const interval = setInterval(() => {
        fetchAllBalanceHistory();
      }, 5 * 60 * 1000); // 5 minutes

      return () => clearInterval(interval);
    }
  }, [connections, selectedPeriods]);

  const fetchConnections = async () => {
    setLoadingConnections(true);
    try {
      // Use keys-with-status endpoint to get connections with balances
      const response = await axios.get(`${backendUrl}/api/exchanges/keys-with-status`, {
        withCredentials: true
      });
      
      // Transform keys to connection format
      const connectionsData = response.data.keys.map(key => ({
        id: key.id,
        exchange_name: key.exchange_name,
        key_name: key.key_name,
        testnet: key.testnet,
        connected_at: key.connected_at,
        balance: key.balance,
        is_active: key.is_active,
        active_setup_title: key.active_setup_title
      }));
      
      setConnections(connectionsData);
    } catch (error) {
      console.error('Error fetching connections:', error);
      toast.error('Ошибка загрузки подключений');
    } finally {
      setLoadingConnections(false);
    }
  };

  const fetchBalance = async (exchangeName) => {
    try {
      const response = await axios.get(
        `${backendUrl}/api/exchanges/balance?exchange_name=${exchangeName}`,
        { withCredentials: true }
      );
      setBalances(prev => ({ ...prev, [exchangeName]: response.data }));
    } catch (error) {
      console.error(`Error fetching balance for ${exchangeName}:`, error);
    }
  };

  const fetchBalanceHistory = async (connectionId, days = 7) => {
    try {
      const response = await axios.get(
        `${backendUrl}/api/exchanges/balance-history?connection_id=${connectionId}&days=${days}`,
        { withCredentials: true }
      );
      // Filter out old records without usdt_total/usdc_total fields
      const validHistory = response.data.history.filter(item => 
        item.usdt_total !== undefined && item.usdc_total !== undefined
      );
      setBalanceHistory(prev => ({ 
        ...prev, 
        [connectionId]: validHistory 
      }));
    } catch (error) {
      console.error(`Error fetching balance history for connection ${connectionId}:`, error);
    }
  };

  const fetchOverallBalanceHistory = async (days = 7) => {
    try {
      // Fetch history without filters to get all connections
      const response = await axios.get(
        `${backendUrl}/api/exchanges/balance-history?days=${days}`,
        { withCredentials: true }
      );
      
      // Filter out old records without usdt_total/usdc_total fields
      const validHistory = response.data.history.filter(item => 
        item.usdt_total !== undefined && item.usdc_total !== undefined
      );
      
      // Group by timestamp and sum USDT and USDC totals
      const historyByTimestamp = {};
      validHistory.forEach(item => {
        const timestamp = item.timestamp;
        if (!historyByTimestamp[timestamp]) {
          historyByTimestamp[timestamp] = {
            timestamp,
            usdt_total: 0,
            usdc_total: 0,
            total: 0
          };
        }
        historyByTimestamp[timestamp].usdt_total += (item.usdt_total || 0);
        historyByTimestamp[timestamp].usdc_total += (item.usdc_total || 0);
        historyByTimestamp[timestamp].total += (item.total || 0);
      });
      
      // Convert to array and sort by timestamp
      const aggregated = Object.values(historyByTimestamp).sort(
        (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
      );
      
      setOverallHistory(aggregated);
    } catch (error) {
      console.error('Error fetching overall balance history:', error);
    }
  };

  const fetchAllBalanceHistory = () => {
    connections.forEach(conn => {
      const days = selectedPeriods[conn.id] || 7;
      fetchBalanceHistory(conn.id, days);
    });
    
    if (connections.length > 1) {
      const overallDays = selectedPeriods['overall'] || 7;
      fetchOverallBalanceHistory(overallDays);
    }
  };

  const handlePeriodChange = (identifier, days) => {
    setSelectedPeriods(prev => ({ ...prev, [identifier]: days }));
    if (identifier === 'overall') {
      fetchOverallBalanceHistory(days);
    } else {
      fetchBalanceHistory(identifier, days);
    }
  };

  const handleConnect = async () => {
    if (!formData.key_name || !formData.api_key || !formData.api_secret) {
      toast.error('Заполните все поля');
      return;
    }

    setConnecting(true);

    try {
      await axios.post(
        `${backendUrl}/api/exchanges/connect`,
        formData,
        { withCredentials: true }
      );

      toast.success('Биржа успешно подключена!');
      setConnectDialogOpen(false);
      setFormData({
        exchange_name: 'okx',
        key_name: '',
        api_key: '',
        api_secret: '',
        testnet: true
      });
      fetchConnections();
    } catch (error) {
      console.error('Error connecting exchange:', error);
      toast.error(error.response?.data?.detail || 'Ошибка подключения биржи');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async (connectionId, keyName) => {
    if (!window.confirm(`Вы уверены, что хотите отключить ключ "${keyName}"?`)) {
      return;
    }

    try {
      await axios.delete(
        `${backendUrl}/api/exchanges/disconnect?connection_id=${connectionId}`,
        { withCredentials: true }
      );

      toast.success('Ключ отключен');
      fetchConnections();
    } catch (error) {
      console.error('Error disconnecting exchange:', error);
      toast.error(error.response?.data?.detail || 'Ошибка отключения ключа');
    }
  };

  if (loading || loadingConnections) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Header />
        <div className="pt-20 pb-8 px-4 md:px-6 max-w-6xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-800 rounded w-1/4"></div>
            <div className="h-64 bg-gray-800 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Toaster position="top-right" />
      <Header />
      
      <div className="pt-20 pb-8 px-4 md:px-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Подключения к биржам</h1>
            <p className="text-gray-400">Управление API ключами для автоматической торговли</p>
          </div>
          
          <div className="flex gap-3">
            <Button
              onClick={() => navigate('/trading-history')}
              variant="outline"
              className="border-blue-600 text-blue-400 hover:bg-blue-900/20"
            >
              История сделок
            </Button>
            <Dialog open={connectDialogOpen} onOpenChange={setConnectDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-[#00ff88] to-[#00cc6f] text-black hover:opacity-90">
                  <Plus className="h-4 w-4 mr-2" />
                  Подключить биржу
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#1a1a1a] border-gray-800 text-white">
              <DialogHeader>
                <DialogTitle className="text-white">Подключить биржу</DialogTitle>
                <DialogDescription className="text-gray-400">
                  Введите API ключи для подключения к бирже
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="exchange" className="text-white">Биржа</Label>
                  <select
                    id="exchange"
                    value={formData.exchange_name}
                    onChange={(e) => setFormData({ ...formData, exchange_name: e.target.value })}
                    className="w-full mt-2 bg-[#0a0a0a] border border-gray-800 rounded-md px-3 py-2 text-white"
                    disabled={connecting}
                  >
                    <option value="okx">OKX ⭐ Рекомендуется (работает из США)</option>
                    <option value="bybit">ByBit (заблокирован в США)</option>
                    <option value="binance">Binance (заблокирован в США)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    ⚠️ Сервер находится в США. Binance и ByBit заблокированы. Используйте OKX.
                  </p>
                  
                  {formData.exchange_name === 'okx' && (
                    <div className="mt-2 p-3 bg-blue-900/20 border border-blue-800 rounded-md">
                      <p className="text-xs text-blue-300 font-semibold mb-2">📋 Как получить OKX API ключи:</p>
                      <ol className="text-xs text-blue-200 space-y-1 list-decimal list-inside">
                        <li>Testnet: <a href="https://www.okx.com/account/my-api" target="_blank" className="underline">okx.com/account/my-api</a></li>
                        <li>Создайте API Key с правами: <strong>Trade</strong></li>
                        <li>Выберите тип: <strong>Trading account</strong></li>
                        <li>Скопируйте API Key и Secret Key</li>
                      </ol>
                    </div>
                  )}
                  
                  {(formData.exchange_name === 'binance' || formData.exchange_name === 'bybit') && (
                    <div className="mt-2 p-3 bg-red-900/20 border border-red-800 rounded-md">
                      <p className="text-xs text-red-300">
                        ⚠️ Эта биржа заблокирована в США. Рекомендуется использовать OKX.
                      </p>
                    </div>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="key_name" className="text-white">Название ключа *</Label>
                  <Input
                    id="key_name"
                    type="text"
                    placeholder='Например: "Основной", "Тест", "Агрессивный"'
                    value={formData.key_name}
                    onChange={(e) => setFormData({ ...formData, key_name: e.target.value })}
                    className="mt-2 bg-[#0a0a0a] border-gray-800 text-white"
                    disabled={connecting}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Уникальное название для этого ключа. Поможет различать несколько ключей одной биржи.
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="api_key" className="text-white">API Key</Label>
                  <Input
                    id="api_key"
                    type="text"
                    placeholder="Ваш API ключ"
                    value={formData.api_key}
                    onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                    className="mt-2 bg-[#0a0a0a] border-gray-800 text-white"
                    disabled={connecting}
                  />
                </div>
                
                <div>
                  <Label htmlFor="api_secret" className="text-white">API Secret</Label>
                  <Input
                    id="api_secret"
                    type="password"
                    placeholder="Ваш API секрет"
                    value={formData.api_secret}
                    onChange={(e) => setFormData({ ...formData, api_secret: e.target.value })}
                    className="mt-2 bg-[#0a0a0a] border-gray-800 text-white"
                    disabled={connecting}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="testnet" className="text-white">Testnet (тестовая сеть)</Label>
                    <p className="text-sm text-gray-400 mt-1">
                      Рекомендуется для тестирования
                    </p>
                  </div>
                  <Switch
                    id="testnet"
                    checked={formData.testnet}
                    onCheckedChange={(checked) => setFormData({ ...formData, testnet: checked })}
                    disabled={connecting}
                  />
                </div>
                
                <div className="bg-blue-900/20 border border-blue-700/50 rounded-md p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-300">
                      <p className="font-semibold mb-1">Важно:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>API ключи шифруются (AES-256)</li>
                        <li>Нужны права на Futures Trading</li>
                        {formData.exchange_name === 'bybit' && (
                          <>
                            <li>ByBit: создайте API в разделе Unified Trading</li>
                            <li>Права: Contract Trade, Wallet</li>
                          </>
                        )}
                        {formData.exchange_name === 'binance' && (
                          <>
                            <li>Binance: может быть заблокирован в вашем регионе</li>
                            <li>Попробуйте ByBit если Binance не работает</li>
                          </>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setConnectDialogOpen(false)}
                    className="flex-1 border-gray-800 text-gray-400 hover:text-white hover:bg-gray-800"
                    disabled={connecting}
                  >
                    Отмена
                  </Button>
                  <Button
                    onClick={handleConnect}
                    className="flex-1 bg-gradient-to-r from-[#00ff88] to-[#00cc6f] text-black hover:opacity-90"
                    disabled={connecting}
                  >
                    {connecting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Подключение...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Подключить
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Connections List */}
        {connections.length === 0 ? (
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="pt-6 text-center">
              <TrendingUp className="h-12 w-12 mx-auto mb-4 text-gray-600" />
              <p className="text-gray-400 mb-4">Нет подключенных бирж</p>
              <p className="text-sm text-gray-500">
                Подключите биржу, чтобы использовать автоматическую торговлю
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Overall Balance Chart (if more than one exchange) */}
            {connections.length > 1 && (
              <div className="mb-6">
                <BalanceChart
                  data={overallHistory}
                  title="Общий баланс по всем биржам"
                  onPeriodChange={(days) => handlePeriodChange('overall', days)}
                  showCurrencies={true}
                />
              </div>
            )}

            {/* Individual Exchange Cards */}
            {connections.map((connection) => {
              const connectionKey = `${connection.exchange_name}_${connection.id || connection.key_name}`;
              const balance = balances[connectionKey];
              const history = balanceHistory[connection.id] || [];
              
              return (
                <Card key={connection.id || connectionKey} className="bg-[#1a1a1a] border-gray-800">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <CardTitle className="text-white text-xl capitalize">
                            {connection.exchange_name}
                          </CardTitle>
                          <Badge variant="outline" className="bg-purple-500/20 text-purple-400 border-purple-500/50">
                            {connection.key_name || 'Ключ 1'}
                          </Badge>
                          {connection.is_active && (
                            <Badge variant="outline" className="bg-orange-500/20 text-orange-400 border-orange-500/50">
                              🔒 Занят: {connection.active_setup_title}
                            </Badge>
                          )}
                        </div>
                        <CardDescription className="text-gray-400 mt-1">
                          Подключено: {new Date(connection.connected_at).toLocaleDateString('ru-RU')}
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant="outline"
                          className={connection.testnet 
                            ? "bg-yellow-500/20 text-yellow-400 border-yellow-500/50"
                            : "bg-[#00ff88]/20 text-[#00ff88] border-[#00ff88]/50"
                          }
                        >
                          {connection.testnet ? 'Testnet' : 'Mainnet'}
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDisconnect(connection.id, connection.key_name || 'Ключ 1')}
                          className="border-red-900/50 text-red-400 hover:text-red-300 hover:bg-red-900/20"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Balance Info */}
                    {connection.balance ? (
                      <div className="space-y-4">
                        {/* Total Balance */}
                        <div className="grid grid-cols-3 gap-4 pb-4 border-b border-gray-800">
                          <div>
                            <p className="text-sm text-gray-400">Общий баланс</p>
                            <p className="text-2xl font-bold text-white">
                              ${connection.balance.total?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || '0'}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-400">Доступно</p>
                            <p className="text-2xl font-bold text-[#00ff88]">
                              ${connection.balance.free?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || '0'}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-400">В позициях</p>
                            <p className="text-2xl font-bold text-blue-400">
                              ${connection.balance.used?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || '0'}
                            </p>
                          </div>
                        </div>
                        
                        {/* USDT and USDC breakdown */}
                        <div className="grid grid-cols-2 gap-4">
                          {/* USDT */}
                          <div className="bg-[#0a0a0a] border border-gray-800 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-2 h-2 rounded-full bg-green-500"></div>
                              <span className="text-sm font-semibold text-gray-300">USDT</span>
                            </div>
                            <div className="space-y-1">
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Всего:</span>
                                <span className="text-white font-medium">
                                  {connection.balance.usdt?.total?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || '0'}
                                </span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Доступно:</span>
                                <span className="text-[#00ff88]">
                                  {connection.balance.usdt?.free?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || '0'}
                                </span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-500">В позициях:</span>
                                <span className="text-blue-400">
                                  {connection.balance.usdt?.used?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || '0'}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          {/* USDC */}
                          <div className="bg-[#0a0a0a] border border-gray-800 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                              <span className="text-sm font-semibold text-gray-300">USDC</span>
                            </div>
                            <div className="space-y-1">
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Всего:</span>
                                <span className="text-white font-medium">
                                  {connection.balance.usdc?.total?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || '0'}
                                </span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Доступно:</span>
                                <span className="text-[#00ff88]">
                                  {connection.balance.usdc?.free?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || '0'}
                                </span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-500">В позициях:</span>
                                <span className="text-blue-400">
                                  {connection.balance.usdc?.used?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || '0'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : connection.error ? (
                      <div className="text-center text-red-400">
                        Ошибка: {connection.error}
                      </div>
                    ) : (
                      <div className="text-center text-gray-500">
                        <Loader2 className="h-6 w-6 mx-auto animate-spin" />
                        <p className="mt-2">Загрузка баланса...</p>
                      </div>
                    )}

                    {/* Balance History Chart */}
                    <div className="mt-6">
                      <BalanceChart
                        data={history}
                        title={`История баланса - ${connection.exchange_name.toUpperCase()} (${connection.key_name})`}
                        onPeriodChange={(days) => handlePeriodChange(connection.id, days)}
                        showCurrencies={true}
                      />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExchangeSettings;
